/**
 * FileUploadZone Component
 *
 * A reusable drag-and-drop file upload component for evidence attachments.
 * Supports multiple files with validation for type and size.
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload,
  InsertDriveFile,
  Close,
  PictureAsPdf,
  Image,
  Description,
  TableChart,
} from '@mui/icons-material';

export interface UploadedFile {
  id: string;
  original_filename: string;
  file_size_bytes: number;
  file_type: string;
  uploaded_by?: string;
  uploaded_at?: string;
  water_entry_id?: string;  // For water attachments
  emission_id?: string;     // For emission attachments
}

interface FileUploadZoneProps {
  files: UploadedFile[];
  onFilesChange: (files: UploadedFile[]) => void;
  onUpload: (files: File[]) => Promise<UploadedFile[]>;
  onRemove?: (fileId: string) => Promise<void>;
  maxFiles?: number;
  maxFileSize?: number; // in bytes
  maxTotalSize?: number; // in bytes
  allowedExtensions?: string[];
  disabled?: boolean;
}

// Default validation settings
const DEFAULT_MAX_FILES = 10;
const DEFAULT_MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB
const DEFAULT_MAX_TOTAL_SIZE = 200 * 1024 * 1024; // 200 MB
const DEFAULT_ALLOWED_EXTENSIONS = ['.pdf', '.xlsx', '.xls', '.csv', '.png', '.jpg', '.jpeg', '.docx', '.doc'];

export const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  files,
  onFilesChange,
  onUpload,
  onRemove,
  maxFiles = DEFAULT_MAX_FILES,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  maxTotalSize = DEFAULT_MAX_TOTAL_SIZE,
  allowedExtensions = DEFAULT_ALLOWED_EXTENSIONS,
  disabled = false,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (fileType: string, filename: string) => {
    const ext = filename.toLowerCase().split('.').pop() || '';

    if (fileType.includes('pdf') || ext === 'pdf') {
      return <PictureAsPdf sx={{ color: '#ef4444', fontSize: 20 }} />;
    }
    if (fileType.includes('image') || ['png', 'jpg', 'jpeg', 'gif'].includes(ext)) {
      return <Image sx={{ color: '#3b82f6', fontSize: 20 }} />;
    }
    if (fileType.includes('spreadsheet') || fileType.includes('excel') || ['xlsx', 'xls', 'csv'].includes(ext)) {
      return <TableChart sx={{ color: '#22c55e', fontSize: 20 }} />;
    }
    if (fileType.includes('word') || ['doc', 'docx'].includes(ext)) {
      return <Description sx={{ color: '#2563eb', fontSize: 20 }} />;
    }
    return <InsertDriveFile sx={{ color: '#6b7280', fontSize: 20 }} />;
  };

  const validateFiles = (selectedFiles: File[]): { valid: File[]; errors: string[] } => {
    const errors: string[] = [];
    const valid: File[] = [];

    // Check total number of files
    if (files.length + selectedFiles.length > maxFiles) {
      errors.push(`Maximum ${maxFiles} files allowed. You already have ${files.length}.`);
      return { valid, errors };
    }

    // Calculate current total size
    const currentTotalSize = files.reduce((sum, f) => sum + f.file_size_bytes, 0);

    let newTotalSize = currentTotalSize;
    for (const file of selectedFiles) {
      // Check extension
      const ext = '.' + file.name.toLowerCase().split('.').pop();
      if (!allowedExtensions.includes(ext)) {
        errors.push(`${file.name}: Invalid file type. Allowed: ${allowedExtensions.join(', ')}`);
        continue;
      }

      // Check individual file size
      if (file.size > maxFileSize) {
        errors.push(`${file.name}: File too large. Maximum ${formatFileSize(maxFileSize)}.`);
        continue;
      }

      // Check total size
      if (newTotalSize + file.size > maxTotalSize) {
        errors.push(`Adding ${file.name} would exceed total size limit of ${formatFileSize(maxTotalSize)}.`);
        continue;
      }

      newTotalSize += file.size;
      valid.push(file);
    }

    return { valid, errors };
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, [disabled]);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await handleFilesSelected(Array.from(e.dataTransfer.files));
    }
  }, [disabled]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      await handleFilesSelected(Array.from(e.target.files));
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleFilesSelected = async (selectedFiles: File[]) => {
    setError(null);

    const { valid, errors } = validateFiles(selectedFiles);

    if (errors.length > 0) {
      setError(errors.join(' '));
    }

    if (valid.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      const uploadedFiles = await onUpload(valid);
      clearInterval(progressInterval);
      setUploadProgress(100);

      // Add to files list
      onFilesChange([...files, ...uploadedFiles]);

      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
      }, 500);
    } catch (err: any) {
      setError(err.message || 'Failed to upload files');
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveFile = async (fileId: string) => {
    if (!onRemove) {
      // Just remove from local state
      onFilesChange(files.filter(f => f.id !== fileId));
      return;
    }

    setRemovingId(fileId);
    try {
      await onRemove(fileId);
      onFilesChange(files.filter(f => f.id !== fileId));
    } catch (err: any) {
      setError(err.message || 'Failed to remove file');
    } finally {
      setRemovingId(null);
    }
  };

  const totalSize = files.reduce((sum, f) => sum + f.file_size_bytes, 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {error && (
        <Alert
          severity="error"
          sx={{ fontSize: '0.75rem', py: 0.5 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Upload Zone */}
      <Paper
        variant="outlined"
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        sx={{
          p: 2,
          textAlign: 'center',
          backgroundColor: disabled ? '#f3f4f6' : dragActive ? '#faf5ff' : '#fafafa',
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: disabled ? '#d1d5db' : dragActive ? '#9333ea' : '#e5e7eb',
          borderRadius: 1.5,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'all 0.2s',
          '&:hover': disabled ? {} : {
            borderColor: '#9333ea',
            backgroundColor: '#faf5ff',
          },
        }}
      >
        {isUploading ? (
          <Box>
            <CircularProgress size={28} sx={{ color: '#9333ea', mb: 1 }} />
            <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.8rem', mb: 1 }}>
              Uploading...
            </Typography>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{
                height: 4,
                borderRadius: 2,
                backgroundColor: '#e5e7eb',
                '& .MuiLinearProgress-bar': { backgroundColor: '#9333ea' },
              }}
            />
          </Box>
        ) : (
          <label style={{ cursor: disabled ? 'not-allowed' : 'pointer', display: 'block' }}>
            <input
              type="file"
              accept={allowedExtensions.join(',')}
              onChange={handleFileSelect}
              multiple
              disabled={disabled || files.length >= maxFiles}
              style={{ display: 'none' }}
            />
            <CloudUpload sx={{ fontSize: 32, color: disabled ? '#9ca3af' : '#9333ea', mb: 0.5 }} />
            <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.8rem', mb: 0.25 }}>
              Drop files here or click to browse
            </Typography>
            <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>
              PDF, Excel, Images, Word • Max {formatFileSize(maxFileSize)} per file • {maxFiles} files max
            </Typography>
          </label>
        )}
      </Paper>

      {/* Uploaded Files List */}
      {files.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {files.map((file) => (
            <Paper
              key={file.id}
              variant="outlined"
              sx={{
                px: 1.5,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderRadius: 1,
                borderColor: '#e5e7eb',
                backgroundColor: '#fafafa',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                {getFileIcon(file.file_type, file.original_filename)}
                <Box sx={{ overflow: 'hidden' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {file.original_filename}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>
                    {formatFileSize(file.file_size_bytes)}
                  </Typography>
                </Box>
              </Box>

              <IconButton
                size="small"
                onClick={() => handleRemoveFile(file.id)}
                disabled={disabled || removingId === file.id}
                sx={{
                  ml: 1,
                  color: '#9ca3af',
                  '&:hover': { color: '#ef4444' },
                }}
              >
                {removingId === file.id ? (
                  <CircularProgress size={16} />
                ) : (
                  <Close sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Paper>
          ))}

          {/* Summary */}
          <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem', textAlign: 'right' }}>
            {files.length} file{files.length !== 1 ? 's' : ''} • {formatFileSize(totalSize)} total
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default FileUploadZone;
